import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from '@mui/material';
import { themeShape } from '@peritasai/ui-shared';
import React, { useEffect, useRef, useState } from 'react';
import {
  DialogCloseButton,
  WarningIcon,
  WarningIconAvatar,
  WarningIconContainer,
} from './dialogShared';

export interface SessionEndingDialogProps {
  open: boolean;
  countdownSeconds: number;
  onContinue: () => void | Promise<void>;
  onLeave: () => void | Promise<void>;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: themeShape.borderRadius.extraLarge,
    maxWidth: '644px',
    width: '100%',
  },
}));
export const SessionEndingDialog: React.FC<SessionEndingDialogProps> = ({
  open,
  countdownSeconds: initialCountdownSeconds,
  onContinue,
  onLeave,
}) => {
  const [countdownSeconds, setCountdownSeconds] = useState(
    initialCountdownSeconds
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deadlineRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      // Clear interval when dialog closes
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      deadlineRef.current = null;
      setCountdownSeconds(initialCountdownSeconds);
      return;
    }

    // When dialog opens, start countdown immediately
    const deadline = Date.now() + initialCountdownSeconds * 1000;
    deadlineRef.current = deadline;
    setCountdownSeconds(initialCountdownSeconds);

    const tick = () => {
      if (!deadlineRef.current) return;

      const remainingSeconds = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000)
      );
      setCountdownSeconds(remainingSeconds);

      if (remainingSeconds <= 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        void onLeave();
      }
    };

    // Start immediately - call tick right away, then set interval for updates
    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, initialCountdownSeconds, onLeave]);

  return (
    <StyledDialog open={open} onClose={onContinue} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          padding: '1.5rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <WarningIconContainer>
          <WarningIconAvatar>
            <WarningIcon />
          </WarningIconAvatar>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            Build Cart Session Ending Soon
          </Typography>
        </WarningIconContainer>
        <DialogCloseButton onClick={onContinue} />
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: '4rem',
        }}
      >
        <Typography
          variant="body1"
          sx={{ pt: '1.5rem', color: 'text.secondary', mb: 2 }}
        >
          Your session is about to time out due to inactivity. Select{' '}
          <strong>Continue Editing</strong> to continue working on this case.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ color: 'text.secondary', mr: 0.5 }}>
            Time remaining:
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} fontWeight="bold">
            {countdownSeconds} Seconds
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          sx={{ textTransform: 'none' }}
          onClick={onLeave}
        >
          Leave Session
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: 'none' }}
          onClick={onContinue}
        >
          Continue Editing
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};
